import React from 'react';
import { XCircle, Mail, Phone, Calendar, Clock, Ban, Pencil } from 'lucide-react';
import { BookingDetail, BookingStatus } from '../../model/professional/booking-model';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezone';
import { fr } from 'date-fns/locale';
import { t } from 'i18next';

// Adapter les statuts de l'API pour l'UI
type UIStatus = BookingStatus | 'canceled';

// Interface Reservation supprimée - nous utilisons directement BookingDetail

interface StatusInfo {
  icon: React.ReactNode;
  color: string;
  label: string;
}

interface ReservationDetailsProps {
  reservation: BookingDetail | null;
  onClose: () => void;
  onEdit?: (booking: BookingDetail) => void;
  getStatusInfo: (status: UIStatus | BookingStatus) => StatusInfo;
}

const ReservationDetails: React.FC<ReservationDetailsProps> = ({
  reservation,
  onClose,
  onEdit,
  getStatusInfo,
}) => {
  if (!reservation) return null;

  // Adapter le statut 'cancelled' à 'canceled' pour l'UI si nécessaire
  const uiStatus = reservation.status === 'cancelled' ? 'canceled' : reservation.status;
  const statusInfo = getStatusInfo(uiStatus);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('reservations.reservationDetails', 'Détails de la réservation')}</h2>
            <button 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              onClick={onClose}
            >
              <XCircle size={20} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">{t('reservations.clientInfo', 'Informations client')}</h3>
              <div className="space-y-3">
                <div className="text-lg font-medium">
                  {reservation.client ? 
                    `${reservation.client.firstName} ${reservation.client.lastName}` : 
                    reservation.clientFirstName && reservation.clientLastName ? 
                    `${reservation.clientFirstName} ${reservation.clientLastName}` : 
                    reservation.clientName ? reservation.clientName : t('reservations.clientNotSpecified', 'Client non spécifié')}
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <Mail size={16} className="mr-2" />
                  {reservation.client?.email || reservation.clientEmail ? (
                    <a 
                      href={`mailto:${reservation.client?.email || reservation.clientEmail}`} 
                      className="hover:underline"
                    >
                      {reservation.client?.email || reservation.clientEmail}
                    </a>
                  ) : t('reservations.emailNotSpecified', 'Email non renseigné')}
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <Phone size={16} className="mr-2" />
                  {reservation.client?.phone || reservation.clientPhone ? (
                    <a 
                      href={`tel:${reservation.client?.phone || reservation.clientPhone}`} 
                      className="hover:underline"
                    >
                      {reservation.client?.phone || reservation.clientPhone || t('reservations.notSpecified', 'Non renseigné')}
                    </a>
                  ) : t('reservations.phoneNotSpecified', 'Téléphone non renseigné')}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">{t('reservations.appointmentInfo', 'Informations rendez-vous')}</h3>
              <div className="space-y-3">
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <Calendar size={16} className="mr-2" />
                  {(() => {
                    try {
                      // Vérifier que startTime est une valeur valide
                      return reservation.startTime ? 
                        formatDateInTimeZone(reservation.startTime, 'EEEE d MMMM yyyy', { locale: fr }) : 
                        t('reservations.dateUnavailable', t('reservations.dateUnavailable', 'Date non disponible'));
                    } catch (error) {
                      console.error('Erreur de formatage de date:', error);
                      return t('reservations.dateUnavailable', t('reservations.dateUnavailable', 'Date non disponible'));
                    }
                  })()}
                </div>
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <Clock size={16} className="mr-2" />
                  {(() => {
                    try {
                      // Vérifier que les dates sont valides
                      if (!reservation.startTime || !reservation.endTime) {
                        return t('reservations.timeUnavailable', 'Horaires non disponibles');
                      }
                      const startTime = formatTimeInTimeZone(reservation.startTime, 'HH:mm');
                      const endTime = formatTimeInTimeZone(reservation.endTime, 'HH:mm');
                      return `${startTime} - ${endTime}`;
                    } catch (error) {
                      console.error('Erreur de formatage d\'heure:', error);
                      return t('reservations.timeUnavailable', 'Horaires non disponibles');
                    }
                  })()}
                  {(reservation.totalDuration || reservation.service?.duration || reservation.serviceDuration) ?
                    ` (${reservation.totalDuration || reservation.service?.duration || reservation.serviceDuration} min)` : ''}
                </div>

                {/* Services - support both multi-services and legacy single service */}
                {reservation.services && reservation.services.length > 0 ? (
                  <div className="space-y-2">
                    {reservation.services.map((service, index) => (
                      <div key={index} className="font-medium">
                        {service.serviceName} - {service.price}€
                        {service.options && service.options.length > 0 && (
                          <ul className="text-sm space-y-1 mt-1 ml-4">
                            {service.options.map(option => (
                              <li key={option.id} className="flex justify-between text-gray-600 dark:text-gray-400">
                                <span>• {option.name}</span>
                                <span className="font-medium">{option.price} {t('services.currency', '€')}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="font-medium">
                    {reservation.service?.name || reservation.serviceName || t('reservations.serviceNotSpecified', 'Service non spécifié')} -
                    {(reservation.service?.price || reservation.servicePrice || 0)}€
                  </div>
                )}

                {/* Legacy options display (only if not using services array) */}
                {!reservation.services && reservation.options && reservation.options.length > 0 && (
                  <div className="mt-2">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('reservations.options', 'Options sélectionnées')}</h4>
                    <ul className="text-sm space-y-1">
                      {reservation.options.map(option => (
                        <li key={option.id} className="flex justify-between">
                          <span>{option.name}</span>
                          <span className="font-medium">{option.price} {t('services.currency', '€')}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 space-y-2">
                  {reservation.status === 'cancelled' ? (
                    // ========== RÉSERVATIONS ANNULÉES ==========
                    <>
                      {reservation.paidAmount !== undefined && reservation.paidAmount > 0 ? (
                        // Il y avait un paiement - afficher les infos de remboursement
                        <>
                          <div className="text-sm text-red-600 dark:text-red-400 font-medium">
                            ⚠️ {t('reservations.refundClient', 'Remboursement client')}
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700 dark:text-gray-300">{t('reservations.refundedAmount', 'Montant remboursé au client')}:</span>
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{reservation.refundAmount ? Number(reservation.refundAmount).toFixed(2) : '0.00'} €</span>
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400 italic">
                            {t('reservations.refundDeductedFromPayments', 'Le remboursement sera automatiquement déduit de vos prochains virements')}
                          </div>
                        </>
                      ) : (
                        // Pas de paiement en ligne - aucun remboursement
                        <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                          ✓ {t('reservations.noRefund', 'Pas de remboursement')}
                        </div>
                      )}
                    </>
                  ) : (
                    // ========== RÉSERVATIONS CONFIRMÉES/COMPLÉTÉES ==========
                    <>
                      {reservation.remainingBalance && Number(reservation.remainingBalance) > 0 ? (
                        // SERVICE AVEC ACOMPTE (basé sur remainingBalance) - afficher 3 lignes
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700 dark:text-gray-300">{t('reservations.totalServicePrice', 'Prix total du service')}:</span>
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{Number(reservation.totalPrice).toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-green-600 dark:text-green-400">{t('reservations.netAmountReceivedDeposit', 'Montant net reçu (acompte)')}:</span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">{reservation.netAmount ? Number(reservation.netAmount).toFixed(2) : (reservation.paidAmount ? Number(reservation.paidAmount).toFixed(2) : '0.00')} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-red-600 dark:text-red-400">{t('reservations.remainingToCollect', 'Solde à encaisser')}:</span>
                            <span className="text-red-600 dark:text-red-400 font-semibold">{Number(reservation.remainingBalance).toFixed(2)} €</span>
                          </div>
                        </>
                      ) : reservation.paidAmount !== undefined && Number(reservation.paidAmount) > 0 ? (
                        // PAIEMENT INTÉGRAL - afficher 1 ligne
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-green-600 dark:text-green-400">{t('reservations.netAmountReceived', 'Montant net reçu')}:</span>
                            <span className="text-green-600 dark:text-green-400 font-semibold">{reservation.netAmount ? Number(reservation.netAmount).toFixed(2) : (reservation.paidAmount ? Number(reservation.paidAmount).toFixed(2) : '0.00')} €</span>
                          </div>
                        </>
                      ) : (
                        // PAS DE PAIEMENT EN LIGNE (abonnement) - afficher 2 lignes
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-700 dark:text-gray-300">{t('reservations.totalPrice', 'Prix total')}:</span>
                            <span className="text-gray-700 dark:text-gray-300 font-medium">{Number(reservation.totalPrice).toFixed(2)} €</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-red-600 dark:text-red-400">{t('reservations.statusToCollect', 'Statut du paiement')}:</span>
                            <span className="text-red-600 dark:text-red-400 font-semibold">{t('reservations.toCollect', 'À encaisser')}</span>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${statusInfo.color}`}>
                    <span className="mr-1">{statusInfo.icon}</span>
                    {statusInfo.label}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {reservation.cancellationReason && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">{t('reservations.cancellationReason', 'Raison d\'annulation')}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{reservation.cancellationReason}</p>
            </div>
          )}
          
          {/* Notes ou instructions spéciales - vérifier si la propriété existe */}
          {(reservation as any).notes && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-700 dark:text-gray-300 mb-2">{t('reservations.specialInstructions', 'Instructions spéciales')}</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{(reservation as any).notes}</p>
            </div>
          )}

          <div className="mt-6 flex justify-between items-center border-t dark:border-gray-700 pt-4">
            <div className="flex gap-2">
              {/* Si la réservation est annulée, on ne peut rien faire */}
              {reservation.status === 'cancelled' && (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic flex items-center">
                  <Ban size={16} className="mr-2 text-red-500" />
                  {t('reservations.reservationCancelled', 'Cette réservation a été annulée')}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {/* Bouton Modifier pour les réservations manuelles */}
              {reservation.isManualBooking && reservation.status !== 'cancelled' && reservation.status !== 'completed' && onEdit && (
                <button
                  onClick={() => onEdit(reservation)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                >
                  <Pencil size={16} />
                  {t('reservations.edit', 'Modifier')}
                </button>
              )}
              <button
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <XCircle size={16} className="mr-2" />
                {t('reservations.close', 'Fermer')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationDetails;
